import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Bus, Users, Loader2, CheckCircle,
  ArrowLeft, FileJson, Navigation, Download, ChevronDown,
  RefreshCw, Clock, Wifi, WifiOff, Calendar,
} from "lucide-react";
import { Button } from "./components/ui/button";
import { ScrollArea } from "./components/ui/scroll-area";
import { MultiDatePicker } from "./DatePicker";

import VehicleTracking from "./VehicleTracking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Per-tab BUID resolution — reads sessionStorage only (same rule as App.js).
// This file is imported lazily so we re-read on every render rather than
// captured-at-import-time, which would go stale after a user switch.
const getBUID = () => sessionStorage.getItem("routeoptima_buid") || "";

// Compact inline BUID badge used in page headers (Planned vs Actual,
// Roster Analysis, Vehicle Tracking). Matches the sidebar badge styling
// but horizontally compact to fit into single-row headers.
function BuidBadge() {
  const buid = getBUID();
  if (!buid) return null;
  return (
    <div
      className="flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-slate-200 bg-slate-50"
      data-testid="header-buid-badge"
      title={`Client: ${buid}`}
    >
      <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Client</span>
      <span className="text-xs font-mono font-semibold text-slate-700 max-w-[180px] truncate">{buid}</span>
    </div>
  );
}

// Sync Status Badge (shown at top with analysis views)
function SyncStatusBadge({ syncStatus }) {
  const auto = syncStatus?.auto_sync;
  const isActive = auto?.is_within_trip_window;
  const isRunning = auto?.status === "running";

  return (
    <div style={{ border: "1px solid #E2E8F0", borderRadius: 8, padding: "12px 14px", background: isActive ? "#F0FDF4" : "#F8FAFC" }} data-testid="sync-status-panel">
      <div className="flex items-center gap-2 mb-2">
        {isRunning ? (
          <RefreshCw className="w-4 h-4 animate-spin" style={{ color: "#16A34A" }} />
        ) : isActive ? (
          <Wifi className="w-4 h-4" style={{ color: "#16A34A" }} />
        ) : (
          <WifiOff className="w-4 h-4" style={{ color: "#94A3B8" }} />
        )}
        <span style={{ fontSize: 13, fontWeight: 600, color: isActive ? "#15803D" : "#64748B" }} data-testid="sync-status-label">
          {isRunning ? "Syncing..." : isActive ? "Auto-Sync Active" : "Auto-Sync Sleeping"}
        </span>
      </div>
      {auto?.current_window && (
        <div style={{ fontSize: 11, color: "#64748B", marginBottom: 4 }} data-testid="sync-window">
          Window: {auto.current_window}
        </div>
      )}
      {auto?.last_run_ago && (
        <div style={{ fontSize: 11, color: "#64748B", marginBottom: 4 }} data-testid="sync-last-run">
          <Clock className="w-3 h-3 inline mr-1" style={{ verticalAlign: "middle" }} />
          Last sync: {auto.last_run_ago}
        </div>
      )}
      {(auto?.gps_count > 0 || auto?.swipe_count > 0) && (
        <div style={{ fontSize: 11, color: "#64748B" }} data-testid="sync-counts">
          Last fetch: {auto.gps_count?.toLocaleString()} GPS pings, {auto.swipe_count?.toLocaleString()} swipes ({auto.imei_count} vehicles)
        </div>
      )}
      {auto?.error_message && (
        <div style={{ fontSize: 11, color: "#DC2626", marginTop: 4 }} data-testid="sync-error">{auto.error_message}</div>
      )}
      {auto?.warning_message && (
        <div style={{ fontSize: 11, color: "#B45309", marginTop: 4, lineHeight: 1.35 }} data-testid="sync-warning">
          ⚠ {auto.warning_message}
        </div>
      )}
      {!auto?.last_run_ago && !isRunning && (
        <div style={{ fontSize: 11, color: "#94A3B8" }}>Waiting for next trip window...</div>
      )}
    </div>
  );
}

// Backfill Panel (shown at bottom of mode selection)
function BackfillPanel({ syncStatus, setSyncStatus, onDataChange }) {
  const [backfillDate, setBackfillDate] = useState("");
  const [backfillLoading, setBackfillLoading] = useState(false);
  const pollRef = useRef(null);
  const toastFiredRef = useRef(false);
  const bf = syncStatus?.backfill;

  // Cleanup interval on unmount
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const handleBackfill = async () => {
    if (!backfillDate) return;
    setBackfillLoading(true);
    toastFiredRef.current = false;
    try {
      await axios.post(`${API}/sync/backfill`, { date: backfillDate });
      toast.success(`Backfill started for ${backfillDate}`);
      pollRef.current = setInterval(async () => {
        try {
          const res = await axios.get(`${API}/sync/status`);
          setSyncStatus(res.data);
          const bfStatus = res.data?.backfill?.status;
          if ((bfStatus === "complete" || bfStatus === "error") && !toastFiredRef.current) {
            toastFiredRef.current = true;
            clearInterval(pollRef.current);
            pollRef.current = null;
            setBackfillLoading(false);
            if (bfStatus === "complete") {
              toast.success(`Backfill complete: ${res.data.backfill.gps_count?.toLocaleString()} GPS, ${res.data.backfill.swipe_count?.toLocaleString()} swipes`);
              // Surface MIS data-quality warnings (e.g. all swipes future-dated → likely wrong BUID)
              if (res.data.backfill.warning_message) {
                toast.warning(res.data.backfill.warning_message, { duration: 12000 });
              }
              if (onDataChange) onDataChange();
            } else {
              toast.error(`Backfill failed: ${res.data.backfill.error_message}`);
            }
          }
        } catch (err) {
          // Swallow transient poll errors — during heavy backfill the
          // backend can briefly return 404/502 and we don't want that
          // to surface as React's "Uncaught runtime error" overlay.
          // Next poll tick (3s) will recover. If the error persists
          // for >30s the user can refresh.
          console.warn("backfill poll transient error:", err?.response?.status || err?.message);
        }
      }, 3000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Backfill failed");
      setBackfillLoading(false);
    }
  };

  return (
    <div style={{ border: "1px solid #E2E8F0", borderRadius: 8, padding: "12px 14px", background: "#FAFBFC" }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#64748B", marginBottom: 8 }}>
        Backfill Historical Data
      </div>
      <div className="flex gap-2 items-center">
        <input
          type="date"
          value={backfillDate}
          onChange={(e) => setBackfillDate(e.target.value)}
          style={{ flex: 1, height: 32, border: "1px solid #E2E8F0", borderRadius: 6, padding: "0 8px", fontSize: 12, color: "#0F172A" }}
          data-testid="backfill-date-input"
        />
        <Button
          size="sm"
          variant="outline"
          className="h-8 text-xs"
          onClick={handleBackfill}
          disabled={!backfillDate || backfillLoading}
          data-testid="backfill-trigger-btn"
        >
          {backfillLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Download className="w-3 h-3 mr-1" />}
          Fetch
        </Button>
      </div>
      {bf?.status === "running" && (
        <div className="mt-2">
          <div className="flex justify-between" style={{ fontSize: 11, color: "#64748B" }}>
            <span>Fetching {bf.date}...</span>
            <span>{bf.progress}%</span>
          </div>
          <div style={{ height: 4, background: "#E2E8F0", borderRadius: 2, marginTop: 4, overflow: "hidden" }}>
            <div style={{ width: `${bf.progress}%`, height: "100%", background: "#3B82F6", borderRadius: 2, transition: "width 0.3s" }} />
          </div>
          {bf.current_window && (
            <div
              data-testid="backfill-current-window"
              style={{ fontSize: 10, color: "#3B82F6", marginTop: 4, fontWeight: 500 }}
            >
              Window {bf.current_window} IST
            </div>
          )}
          <div style={{ fontSize: 10, color: "#94A3B8", marginTop: 2 }}>
            {bf.gps_count?.toLocaleString()} GPS, {bf.swipe_count?.toLocaleString()} swipes so far
          </div>
        </div>
      )}
      {bf?.status === "complete" && bf?.date && (
        <div style={{ fontSize: 11, color: "#16A34A", marginTop: 4 }} data-testid="backfill-complete">
          <CheckCircle className="w-3 h-3 inline mr-1" style={{ verticalAlign: "middle" }} />
          {bf.date}: {bf.gps_count?.toLocaleString()} GPS, {bf.swipe_count?.toLocaleString()} swipes
        </div>
      )}
      {bf?.status === "error" && (
        <div style={{ fontSize: 11, color: "#DC2626", marginTop: 4 }}>{bf.error_message}</div>
      )}
    </div>
  );
}

// Multi-select dropdown
function MultiSelect({ label, options, selected, onChange, allLabel = "All" }) {
  const [open, setOpen] = useState(false);
  const [interacted, setInteracted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const defaultAll = !interacted && selected.length === 0;
  const allChecked = (options.length > 0 && selected.length === options.length) || defaultAll;

  const displayText = allChecked
    ? `${allLabel} (${options.length})`
    : selected.length === 0
      ? "None selected"
      : selected.length === 1 ? selected[0] : `${selected.length} selected`;

  const fire = (val) => { if (!interacted) setInteracted(true); onChange(val); };

  const toggle = (val) => {
    if (defaultAll) {
      fire(options.filter(o => o !== val));
    } else if (selected.includes(val)) {
      fire(selected.filter(v => v !== val));
    } else {
      fire([...selected, val]);
    }
  };

  const handleSelectAll = () => {
    if (allChecked) fire([]);
    else fire([...options]);
  };

  const isChecked = (opt) => defaultAll ? true : selected.includes(opt);

  return (
    <div className="relative" ref={ref}>
      <div style={{ fontFamily: "'Barlow', sans-serif", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#64748B", marginBottom: 6 }}>{label}</div>
      <button
        className="w-full px-3 border rounded-md bg-white text-left flex items-center justify-between hover:border-slate-300 transition-colors"
        style={{ height: 36, borderColor: "#E2E8F0", fontSize: 13, color: "#0F172A" }}
        onClick={() => setOpen(!open)}
        data-testid={`filter-${label.toLowerCase()}`}
      >
        <span className="truncate">{displayText}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} style={{ color: "#64748B" }} />
      </button>
      {open && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto" style={{ borderColor: "#E2E8F0" }}>
          <label className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 cursor-pointer border-b" style={{ borderColor: "#E2E8F0" }}>
            <input type="checkbox" checked={allChecked} onChange={handleSelectAll} className="rounded" data-testid={`select-all-${label.toLowerCase()}`} />
            <span style={{ fontSize: 12, fontWeight: 600, color: "#0F172A" }}>Select All</span>
          </label>
          {options.map(opt => (
            <label key={opt} className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 cursor-pointer">
              <input type="checkbox" checked={isChecked(opt)} onChange={() => toggle(opt)} className="rounded" />
              <span style={{ fontSize: 12, color: "#0F172A" }}>{opt}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// Single select
function SingleSelect({ label, options, value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const displayText = options.find(o => o.value === value)?.label || value;

  return (
    <div className="relative" ref={ref}>
      <div style={{ fontFamily: "'Barlow', sans-serif", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#64748B", marginBottom: 6 }}>{label}</div>
      <button
        className="w-full px-3 border rounded-md bg-white text-left flex items-center justify-between hover:border-slate-300 transition-colors"
        style={{ height: 36, borderColor: "#E2E8F0", fontSize: 13, color: "#0F172A" }}
        onClick={() => setOpen(!open)}
        data-testid={`filter-${label.toLowerCase()}`}
      >
        <span className="truncate">{displayText}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} style={{ color: "#64748B" }} />
      </button>
      {open && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border rounded-md shadow-lg" style={{ borderColor: "#E2E8F0" }}>
          {options.map(opt => (
            <button
              key={opt.value}
              className="w-full text-left px-3 py-1.5 hover:bg-slate-50"
              style={{ fontSize: 12, color: "#0F172A", fontWeight: value === opt.value ? 600 : 400, background: value === opt.value ? "#F8FAFC" : undefined }}
              onClick={() => { onChange(opt.value); setOpen(false); }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ComparisonView({ onBack }) {
  const [compareStatus, setCompareStatus] = useState(null);
  const [mode, setMode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [filterDates, setFilterDates] = useState([]);
  const [filterShifts, setFilterShifts] = useState([]);
  const [filterDirection, setFilterDirection] = useState("IN");
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [filterHostels, setFilterHostels] = useState([]);
  const [availDates, setAvailDates] = useState([]);
  const [availShifts, setAvailShifts] = useState([]);
  const [availHostels, setAvailHostels] = useState([]);
  const [activeTab, setActiveTab] = useState("summary");
  const [autoLoaded, setAutoLoaded] = useState(false);
  const [drillMetric, setDrillMetric] = useState(null);
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillParams, setDrillParams] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/compare/status`);
      setCompareStatus(res.data);
    } catch (e) { console.error(e); }
  }, []);

  const fetchSyncStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/sync/status`);
      setSyncStatus(res.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchStatus(); fetchSyncStatus(); }, [fetchStatus, fetchSyncStatus]);
  useEffect(() => {
    const interval = setInterval(fetchSyncStatus, 15000);
    return () => clearInterval(interval);
  }, [fetchSyncStatus]);

  const bothUploaded = compareStatus?.gps_uploaded && compareStatus?.swipe_uploaded;
  const canEmployee = compareStatus?.swipe_uploaded;
  const canVehicle = compareStatus?.gps_uploaded && compareStatus?.swipe_uploaded;

  // Compute current week dates (Monday to today)
  const getCurrentWeekDates = useCallback(() => {
    const today = new Date();
    const day = today.getDay();
    const monday = new Date(today);
    monday.setDate(today.getDate() - (day === 0 ? 6 : day - 1));
    const dates = [];
    const d = new Date(monday);
    while (d <= today) {
      dates.push(d.toISOString().split("T")[0]);
      d.setDate(d.getDate() + 1);
    }
    return dates;
  }, []);

  const handleRunAnalysis = useCallback(async (overrideDates, overrideDirection) => {
    setLoading(true);
    try {
      const dates = overrideDates || filterDates;
      const direction = overrideDirection || filterDirection;

      // Multi-date: compute all dates in parallel (each per-date request
      // hits the backend compute cache on repeat clicks). Progress bar
      // increments as each request resolves so the UI feels responsive
      // during a 7-day initial load. Sequential chains used to take
      // 7 × ~5s = ~35s on Atlas; parallel pulls that down to max(per-call)
      // + the client's limit of 6 concurrent axios connections.
      if (dates.length > 1) {
        const merged = {
          summary: {},
          rows_by_date_shift_direction: [],
          rows_by_hostel: [],
          available_dates: [],
          available_shifts: [],
          available_hostels: [],
        };
        const hostelAgg = {};
        const dateSet = new Set();
        const shiftSet = new Set();
        const hostelSet = new Set();

        setProgress({ current: 0, total: dates.length });
        let done = 0;

        const perDate = await Promise.all(dates.map(async (d) => {
          const body = { dates: [d] };
          if (filterShifts.length > 0) body.shifts = filterShifts;
          if (direction !== "ALL") body.direction = direction;
          if (filterHostels.length > 0) body.hostels = filterHostels;
          try {
            const res = await axios.post(`${API}/compare/employee-analysis`, body);
            done += 1;
            setProgress({ current: done, total: dates.length });
            return res.data;
          } catch (err) {
            done += 1;
            setProgress({ current: done, total: dates.length });
            return null;
          }
        }));

        for (const data of perDate) {
          if (!data) continue;
          Object.keys(data.summary || {}).forEach((k) => {
            merged.summary[k] = (merged.summary[k] || 0) + (data.summary[k] || 0);
          });
          (data.rows_by_date_shift_direction || []).forEach((r) => merged.rows_by_date_shift_direction.push(r));
          (data.rows_by_hostel || []).forEach((row) => {
            const h = row.hostel;
            if (!hostelAgg[h]) hostelAgg[h] = { hostel: h };
            Object.keys(row).forEach((k) => {
              if (k === "hostel") return;
              hostelAgg[h][k] = (hostelAgg[h][k] || 0) + (row[k] || 0);
            });
          });
          (data.available_dates || []).forEach((x) => dateSet.add(x));
          (data.available_shifts || []).forEach((x) => shiftSet.add(x));
          (data.available_hostels || []).forEach((x) => hostelSet.add(x));
        }

        merged.rows_by_hostel = Object.values(hostelAgg).sort((a, b) =>
          (a.hostel || "").localeCompare(b.hostel || ""),
        );
        merged.available_dates = Array.from(dateSet).sort();
        merged.available_shifts = Array.from(shiftSet).sort();
        merged.available_hostels = Array.from(hostelSet).sort();

        setResults(merged);
        if (merged.available_dates.length > 0) setAvailDates(prev => prev.length > 0 ? prev : merged.available_dates);
        if (merged.available_shifts.length > 0) setAvailShifts(prev => prev.length > 0 ? prev : merged.available_shifts);
        if (merged.available_hostels.length > 0) setAvailHostels(prev => prev.length > 0 ? prev : merged.available_hostels);
        return;
      }

      // Single-date / no-date fast path: original behaviour.
      const body = {};
      if (dates.length > 0) body.dates = dates;
      if (filterShifts.length > 0) body.shifts = filterShifts;
      if (direction !== "ALL") body.direction = direction;
      if (filterHostels.length > 0) body.hostels = filterHostels;
      const res = await axios.post(`${API}/compare/employee-analysis`, body);
      setResults(res.data);
      if (res.data.available_dates?.length > 0) setAvailDates(prev => prev.length > 0 ? prev : res.data.available_dates);
      if (res.data.available_shifts?.length > 0) setAvailShifts(prev => prev.length > 0 ? prev : res.data.available_shifts);
      if (res.data.available_hostels?.length > 0) setAvailHostels(prev => prev.length > 0 ? prev : res.data.available_hostels);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Analysis failed");
    } finally {
      setLoading(false);
      setProgress({ current: 0, total: 0 });
    }
  }, [filterDates, filterShifts, filterDirection, filterHostels]);

  const handleDrill = useCallback(async (metric, rowOverrides) => {
    setDrillLoading(true);
    setDrillMetric(metric);
    try {
      const body = { metric };
      // Always start with the current filter state as baseline
      if (filterDates.length > 0) body.dates = filterDates;
      if (filterShifts.length > 0) body.shifts = filterShifts;
      if (filterDirection !== "ALL") body.direction = filterDirection;
      if (filterHostels.length > 0) body.hostels = filterHostels;
      // Apply row-specific overrides on top (narrowing further)
      if (rowOverrides) {
        if (rowOverrides.date) body.dates = [rowOverrides.date];
        if (rowOverrides.shift) body.shifts = [rowOverrides.shift];
        if (rowOverrides.direction) body.direction = rowOverrides.direction;
        if (rowOverrides.hostel) body.hostels = [rowOverrides.hostel];
      }
      setDrillParams(body);
      const res = await axios.post(`${API}/compare/employee-detail`, body);
      setDrillData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load detail");
      setDrillMetric(null);
    } finally { setDrillLoading(false); }
  }, [filterDates, filterShifts, filterDirection, filterHostels]);

  // When filters change while drill-down is active, also refresh the detail view
  const handleRunWithDrill = useCallback(async () => {
    await handleRunAnalysis();
    if (drillMetric) {
      handleDrill(drillMetric);
    }
  }, [handleRunAnalysis, drillMetric, handleDrill]);

  // Auto-load when entering employee mode with current week + IN direction
  useEffect(() => {
    if (mode === "employee" && canEmployee && !autoLoaded) {
      const weekDates = getCurrentWeekDates();
      setFilterDates(weekDates);
      setFilterDirection("IN");
      setAutoLoaded(true);
      handleRunAnalysis(weekDates, "IN");
    }
  }, [mode, canEmployee, autoLoaded, getCurrentWeekDates, handleRunAnalysis]);

  const fmt = (n) => (n || 0).toLocaleString();

  const handleDownloadDetail = useCallback(async () => {
    if (!drillData?.employees?.length || !drillParams) return;
    setDownloadLoading(true);
    try {
      const res = await axios.post(`${API}/compare/employee-detail-download`, drillParams, { responseType: "blob", timeout: 120000 });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `employee_detail_${drillMetric}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed");
    } finally {
      setDownloadLoading(false);
    }
  }, [drillData, drillMetric, drillParams]);

  const METRIC_LABELS = {
    total_rostered: "Total Rostered",
    total_boardings: "Total Boardings",
    rostered_and_boarded: "Rostered & Boarded",
    rostered_not_boarded: "Rostered Not Boarded",
    not_rostered_but_boarded: "Not Rostered But Boarded",
    rostered_boarded_right_stop: "Rostered & Boarded in Right Stop",
    rostered_boarded_wrong_stop: "Rostered & Boarded in Wrong Stop",
    rostered_boarded_right_shift: "Rostered & Boarded in Right Shift",
    rostered_boarded_wrong_shift: "Rostered & Boarded in Wrong Shift",
  };

  const STATUS_COLORS = {
    "Right Stop": { bg: "#DCFCE7", text: "#15803D" },
    "Wrong Stop": { bg: "#FEF3C7", text: "#B45309" },
    "Right Shift": { bg: "#DBEAFE", text: "#1D4ED8" },
    "Wrong Shift": { bg: "#F3E8FF", text: "#7C3AED" },
    "Rostered not Travelled": { bg: "#FEE2E2", text: "#DC2626" },
    "Not Rostered": { bg: "#FFF7ED", text: "#EA580C" },
  };

  // ---- Mode Selection ----
  if (!mode) {
    return (
      <div className="h-screen flex flex-col" style={{ background: "#F8FAFC" }} data-testid="comparison-view">
        <div className="flex items-center justify-between gap-3" style={{ padding: "12px 20px", background: "#FAFBFC", borderBottom: "1px solid #E2E8F0" }}>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onBack} data-testid="comparison-back-btn">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 style={{ fontFamily: "'Barlow', sans-serif", fontSize: 18, fontWeight: 700, color: "#0F172A" }}>Planned vs Actual</h1>
              <p style={{ fontSize: 12, color: "#64748B" }}>Compare rostered data with actual boardings</p>
            </div>
          </div>
          <BuidBadge />
        </div>
        <div className="flex-1 flex flex-col items-center justify-between" style={{ padding: 24 }}>
          <div style={{ width: "100%", maxWidth: 480 }}>
            <div style={{ fontFamily: "'Barlow', sans-serif", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#64748B", marginBottom: 12 }}>Data Sync</div>
            <div className="mb-8">
              <SyncStatusBadge syncStatus={syncStatus} />
            </div>
            <div style={{ fontFamily: "'Barlow', sans-serif", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#64748B", marginBottom: 12 }}>Choose Analysis View</div>
            <div className="grid grid-cols-2 gap-4">
              <button
                className="text-center transition-all"
                style={{ border: `2px solid ${canVehicle ? "#E2E8F0" : "#F1F5F9"}`, borderRadius: 8, padding: 24, background: "white", opacity: canVehicle ? 1 : 0.45, cursor: canVehicle ? "pointer" : "not-allowed" }}
                onClick={() => canVehicle && setMode("vehicle")}
                disabled={!canVehicle}
                data-testid="mode-vehicle"
              >
                <Bus className="w-7 h-7 mx-auto mb-2" style={{ color: "#64748B" }} />
                <div style={{ fontSize: 14, fontWeight: 600, color: "#0F172A" }}>Vehicle</div>
                <div style={{ fontSize: 11, color: "#64748B", marginTop: 4 }}>GPS traces & routes</div>
              </button>
              <button
                className="text-center transition-all"
                style={{ border: `2px solid ${canEmployee ? "#E2E8F0" : "#F1F5F9"}`, borderRadius: 8, padding: 24, background: "white", opacity: canEmployee ? 1 : 0.45, cursor: canEmployee ? "pointer" : "not-allowed" }}
                onClick={() => canEmployee && setMode("employee")}
                disabled={!canEmployee}
                data-testid="mode-employee"
              >
                <Users className="w-7 h-7 mx-auto mb-2" style={{ color: "#64748B" }} />
                <div style={{ fontSize: 14, fontWeight: 600, color: "#0F172A" }}>Employee</div>
                <div style={{ fontSize: 11, color: "#64748B", marginTop: 4 }}>Roster vs boardings</div>
              </button>
            </div>
            {!canEmployee && <p style={{ fontSize: 11, color: "#94A3B8", textAlign: "center", marginTop: 12 }}>Waiting for swipe data sync to enable Employee analysis</p>}
            {canEmployee && !canVehicle && <p style={{ fontSize: 11, color: "#94A3B8", textAlign: "center", marginTop: 12 }}>Waiting for GPS data sync to enable Vehicle analysis</p>}
          </div>
          <div style={{ width: "100%", maxWidth: 480, paddingBottom: 24 }}>
            <BackfillPanel syncStatus={syncStatus} setSyncStatus={setSyncStatus} onDataChange={fetchStatus} />
          </div>
        </div>
      </div>
    );
  }

  // ---- Vehicle Tracking ----
  if (mode === "vehicle") {
    return <VehicleTracking onBack={() => setMode(null)} />;
  }

  // ---- Employee Analysis Dashboard ----
  return (
    <div className="h-screen flex flex-col" style={{ background: "#F8FAFC" }} data-testid="comparison-view">
      {/* Header */}
      <div className="flex items-center justify-between" style={{ padding: "12px 24px", background: "#FAFBFC", borderBottom: "1px solid #E2E8F0" }}>
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setMode(null)} data-testid="comparison-back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 style={{ fontFamily: "'Barlow', sans-serif", fontSize: 20, fontWeight: 700, color: "#0F172A", letterSpacing: "-0.01em" }}>
              Roster Analysis
            </h1>
            <p style={{ fontSize: 12, color: "#64748B" }}>Rostered vs Actual Boardings</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <BuidBadge />
          <img
            src="https://customer-assets.emergentagent.com/job_abef7da1-444b-465c-8a83-2cd66a76002d/artifacts/hw3dtw1t_MIS%20Horizontal.png"
            alt="MoveInSync" style={{ height: 36 }}
          />
        </div>
      </div>

      {/* Content */}
      <ScrollArea className="flex-1">
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "20px 24px" }}>

          {/* Filters */}
          <div style={{ background: "white", border: "1px solid #E2E8F0", borderRadius: 6, padding: 20, marginBottom: 20 }}>
            <div className="grid grid-cols-4 gap-4 items-end">
              {/* Calendar-driven date pick — user can select any number of
                  dates regardless of what data exists. The compute runs on
                  Apply; an empty result for a given date is rendered as
                  "no data" rather than hidden behind a filtered dropdown. */}
              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#64748B", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>Date</label>
                <MultiDatePicker
                  value={filterDates}
                  onChange={setFilterDates}
                  placeholder="Select dates"
                  disabled={loading}
                  data-testid="filter-dates"
                />
              </div>
              <fieldset disabled={loading} style={{ minWidth: 0 }}>
                <MultiSelect label="Shift" options={availShifts.length > 0 ? availShifts : ["A", "B", "C"]} selected={filterShifts} onChange={setFilterShifts} allLabel="All" />
              </fieldset>
              <fieldset disabled={loading} style={{ minWidth: 0 }}>
                <SingleSelect label="Direction" value={filterDirection} onChange={setFilterDirection} options={[
                  { value: "ALL", label: "All" },
                  { value: "IN", label: "IN (Login)" },
                  { value: "OUT", label: "OUT (Logout)" },
                ]} />
              </fieldset>
              <fieldset disabled={loading} style={{ minWidth: 0 }}>
                <MultiSelect label="Stop" options={availHostels} selected={filterHostels} onChange={setFilterHostels} allLabel="All selected" />
              </fieldset>
            </div>
            <div className="flex justify-end mt-4">
              <Button
                onClick={() => handleRunWithDrill()}
                disabled={loading}
                style={{ height: 36, paddingLeft: 20, paddingRight: 20, background: "#0F172A", color: "white", fontSize: 13, fontWeight: 600 }}
                data-testid="run-analysis-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                {loading
                  ? (progress.total > 1
                      ? `Computing ${progress.current}/${progress.total}…`
                      : "Applying…")
                  : "Apply"}
              </Button>
            </div>

            {loading && progress.total > 1 && (
              <div style={{ marginTop: 12 }} data-testid="progress-bar-container">
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#64748B", marginBottom: 4 }}>
                  <span>Computing data per date</span>
                  <span data-testid="progress-bar-text">{progress.current} of {progress.total}</span>
                </div>
                <div style={{ height: 6, background: "#E2E8F0", borderRadius: 999, overflow: "hidden" }}>
                  <div
                    data-testid="progress-bar-fill"
                    style={{
                      width: `${Math.round((progress.current / progress.total) * 100)}%`,
                      height: "100%",
                      background: "#0F172A",
                      transition: "width 0.3s ease",
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Summary Metrics */}
          {results && !drillMetric && (
            <div style={{ background: "white", border: "1px solid #E2E8F0", borderRadius: 6, padding: "20px 24px", marginBottom: 20 }}>
              {/* Row 1 */}
              <div className="flex items-center" style={{ marginBottom: 16 }}>
                <div className="grid grid-cols-5 gap-6 flex-1 text-center">
                  {[
                    { label: "Total Rostered", val: results.summary.total_rostered, color: "#0F172A", metric: "total_rostered" },
                    { label: "Total Boardings", val: results.summary.total_boardings, color: "#0F172A", metric: "total_boardings" },
                    { label: "Rostered & Boarded", val: results.summary.rostered_and_boarded, color: "#16A34A", metric: "rostered_and_boarded" },
                    { label: "Rostered Not Boarded", val: results.summary.rostered_not_boarded, color: "#DC2626", metric: "rostered_not_boarded" },
                    { label: "Not Rostered But Boarded", val: results.summary.not_rostered_but_boarded, color: "#EA580C", metric: "not_rostered_but_boarded" },
                  ].map((m, i) => (
                    <div key={i} data-testid={`metric-${m.label.replace(/\s+/g, "-").toLowerCase()}`}>
                      <div style={{ fontFamily: "'Barlow', sans-serif", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#64748B", marginBottom: 6 }}>{m.label}</div>
                      <div
                        onClick={() => m.val > 0 && handleDrill(m.metric)}
                        style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 22, fontWeight: 600, color: m.color, cursor: m.val > 0 ? "pointer" : "default", textDecoration: m.val > 0 ? "underline" : "none", textDecorationStyle: "dotted", textUnderlineOffset: 4 }}
                        data-testid={`drill-${m.metric}`}
                      >
                        {fmt(m.val)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {/* Divider */}
              <div style={{ borderTop: "1px solid #E2E8F0", paddingTop: 16 }}>
                <div className="grid grid-cols-4 gap-6 text-center">
                  {[
                    { label: "Rostered & Boarded in Right Stop", val: results.summary.rostered_boarded_right_stop, color: "#16A34A", metric: "rostered_boarded_right_stop" },
                    { label: "Rostered & Boarded in Wrong Stop", val: results.summary.rostered_boarded_wrong_stop, color: "#EA580C", metric: "rostered_boarded_wrong_stop" },
                    { label: "Rostered & Boarded in Right Shift", val: results.summary.rostered_boarded_right_shift, color: "#2563EB", metric: "rostered_boarded_right_shift" },
                    { label: "Rostered & Boarded in Wrong Shift", val: results.summary.rostered_boarded_wrong_shift, color: "#9333EA", metric: "rostered_boarded_wrong_shift" },
                  ].map((m, i) => (
                    <div key={i} data-testid={`metric-${m.label.replace(/\s+/g, "-").toLowerCase()}`}>
                      <div style={{ fontFamily: "'Barlow', sans-serif", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#64748B", marginBottom: 6 }}>{m.label}</div>
                      <div
                        onClick={() => m.val > 0 && handleDrill(m.metric)}
                        style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 22, fontWeight: 600, color: m.color, cursor: m.val > 0 ? "pointer" : "default", textDecoration: m.val > 0 ? "underline" : "none", textDecorationStyle: "dotted", textUnderlineOffset: 4 }}
                        data-testid={`drill-${m.metric}`}
                      >
                        {fmt(m.val)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Drill-down Employee Detail */}
          {drillMetric && (
            <div style={{ marginBottom: 20 }}>
              {/* Drill header */}
              <div className="flex items-center justify-between" style={{ background: "white", border: "1px solid #E2E8F0", borderRadius: 6, padding: "16px 24px", marginBottom: 12 }}>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => { setDrillMetric(null); setDrillData(null); setDrillParams(null); }}
                    style={{ fontSize: 13, fontWeight: 600, color: "#64748B", display: "flex", alignItems: "center", gap: 6 }}
                    data-testid="drill-back-btn"
                  >
                    <ArrowLeft className="w-4 h-4" /> Back to Summary
                  </button>
                  <div style={{ width: 1, height: 24, background: "#E2E8F0" }} />
                  <div>
                    <div style={{ fontFamily: "'Barlow', sans-serif", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#64748B" }}>
                      {METRIC_LABELS[drillMetric] || drillMetric}
                    </div>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 20, fontWeight: 600, color: "#0F172A" }}>
                      {drillLoading ? "..." : fmt(drillData?.total)}
                      <span style={{ fontFamily: "'Barlow', sans-serif", fontSize: 12, fontWeight: 400, color: "#64748B", marginLeft: 8 }}>employees</span>
                    </div>
                  </div>
                </div>
                <Button
                  onClick={handleDownloadDetail}
                  disabled={downloadLoading}
                  style={{ height: 40, paddingLeft: 20, paddingRight: 20, background: "#0F172A", color: "white", fontSize: 13, fontWeight: 600 }}
                  data-testid="download-detail-btn"
                >
                  {downloadLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  {downloadLoading ? "Downloading..." : "Download"}
                </Button>
              </div>

              {/* Drill table */}
              {drillLoading ? (
                <div style={{ background: "white", border: "1px solid #E2E8F0", borderRadius: 6, padding: "48px 24px", textAlign: "center" }}>
                  <Loader2 className="w-8 h-8 mx-auto animate-spin" style={{ color: "#64748B" }} />
                  <div style={{ fontSize: 13, color: "#64748B", marginTop: 12 }}>Loading employee details...</div>
                </div>
              ) : drillData?.employees?.length > 0 ? (
                <div style={{ background: "white", border: "1px solid #E2E8F0", borderRadius: 6, overflow: "hidden" }}>
                  <div className="overflow-x-auto">
                    <table style={{ width: "100%", borderCollapse: "collapse" }} data-testid="drill-table">
                      <thead>
                        <tr style={{ background: "#22C55E" }}>
                          {["Emp ID", "Name", "RFID No", "Date", "Shift", "Direction", "Stop", "Actual Shift", "Actual Stop", "Status"].map(col => (
                            <th key={col} style={{ padding: "12px 16px", textAlign: "left", fontFamily: "'Barlow', sans-serif", fontSize: 12, fontWeight: 700, color: "white", whiteSpace: "nowrap" }}>{col}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {drillData.employees.map((emp, i) => (
                          <tr key={i} style={{ borderBottom: "1px solid #F1F5F9" }}>
                            <td style={{ padding: "10px 16px", fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 500, color: "#0F172A" }}>{emp.emp_id || "-"}</td>
                            <td style={{ padding: "10px 16px", fontSize: 12, color: "#0F172A" }}>{emp.name || "-"}</td>
                            <td style={{ padding: "10px 16px", fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: "#64748B" }}>{emp.rfid || "-"}</td>
                            <td style={{ padding: "10px 16px", fontSize: 12, color: "#0F172A" }}>{emp.date}</td>
                            <td style={{ padding: "10px 16px", fontSize: 12, fontWeight: 500, color: "#0F172A" }}>{emp.shift || "-"}</td>
                            <td style={{ padding: "10px 16px", fontSize: 12, color: "#0F172A" }}>{emp.direction}</td>
                            <td style={{ padding: "10px 16px", fontSize: 12, color: "#0F172A" }}>{emp.stop || "-"}</td>
                            <td style={{ padding: "10px 16px", fontSize: 12, color: "#0F172A" }}>{emp.actual_shift || "-"}</td>
                            <td style={{ padding: "10px 16px", fontSize: 12, color: "#0F172A" }}>{emp.actual_stop || "-"}</td>
                            <td style={{ padding: "10px 16px" }}>
                              <div className="flex flex-wrap gap-1">
                                {emp.statuses.map((s, j) => (
                                  <span key={j} style={{
                                    display: "inline-block", padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 600,
                                    background: (STATUS_COLORS[s] || { bg: "#F1F5F9" }).bg,
                                    color: (STATUS_COLORS[s] || { text: "#64748B" }).text,
                                  }}>{s}</span>
                                ))}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {drillData.total > 5000 && (
                    <div style={{ padding: "12px 24px", borderTop: "1px solid #E2E8F0", fontSize: 12, color: "#64748B", textAlign: "center" }}>
                      Showing first 5,000 of {fmt(drillData.total)} employees
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ background: "white", border: "1px solid #E2E8F0", borderRadius: 6, padding: "48px 24px", textAlign: "center" }}>
                  <div style={{ fontSize: 14, fontWeight: 500, color: "#0F172A" }}>No employee records found</div>
                  <div style={{ fontSize: 12, color: "#64748B", marginTop: 4 }}>Try adjusting your filters</div>
                </div>
              )}
            </div>
          )}

          {/* Table Section - only when not drilling */}
          {results && !drillMetric && (
            <div style={{ background: "white", border: "1px solid #E2E8F0", borderRadius: 6, overflow: "hidden" }}>
              {/* Tabs */}
              <div className="flex" style={{ borderBottom: "1px solid #E2E8F0" }}>
                {["summary", "hostel"].map(tab => (
                  <button
                    key={tab}
                    className="transition-colors"
                    style={{
                      padding: "12px 20px",
                      fontSize: 13,
                      fontWeight: activeTab === tab ? 600 : 400,
                      color: activeTab === tab ? "#0F172A" : "#64748B",
                      borderBottom: activeTab === tab ? "2px solid #0F172A" : "2px solid transparent",
                      background: "transparent",
                    }}
                    onClick={() => setActiveTab(tab)}
                    data-testid={`tab-${tab}`}
                  >
                    {tab === "summary" ? "Summary by Date, Shift & Direction" : "Detailed Breakdown by Stop"}
                  </button>
                ))}
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                {activeTab === "summary" ? (
                  <table className="results-table" data-testid="summary-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Shift</th>
                        <th>Direction</th>
                        <th style={{ textAlign: "right" }}>Rostered</th>
                        <th style={{ textAlign: "right" }}>Boardings</th>
                        <th style={{ textAlign: "right" }}>Right Stop</th>
                        <th style={{ textAlign: "right" }}>Wrong Stop</th>
                        <th style={{ textAlign: "right" }}>Not Boarded</th>
                        <th style={{ textAlign: "right" }}>Not Rostered</th>
                        <th style={{ textAlign: "right" }}>Right Shift</th>
                        <th style={{ textAlign: "right" }}>Wrong Shift</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.rows_by_date_shift_direction?.map((r, i) => {
                        const rov = { date: r.date, shift: r.shift, direction: r.direction };
                        const cell = (val, metric, color) => (
                          <td
                            onClick={() => val > 0 && handleDrill(metric, rov)}
                            style={{ textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 500, color, cursor: val > 0 ? "pointer" : "default", textDecoration: val > 0 ? "underline" : "none", textDecorationStyle: "dotted", textUnderlineOffset: 3 }}
                          >{fmt(val)}</td>
                        );
                        return (
                          <tr key={i}>
                            <td>{r.display_date}</td>
                            <td>{r.shift}</td>
                            <td>{r.direction}</td>
                            {cell(r.total_rostered, "total_rostered", "#0F172A")}
                            {cell(r.total_boardings, "total_boardings", "#0F172A")}
                            {cell(r.rostered_boarded_right_stop, "rostered_boarded_right_stop", "#16A34A")}
                            {cell(r.rostered_boarded_wrong_stop, "rostered_boarded_wrong_stop", "#EA580C")}
                            {cell(r.rostered_not_boarded, "rostered_not_boarded", "#DC2626")}
                            {cell(r.not_rostered_but_boarded, "not_rostered_but_boarded", "#EA580C")}
                            {cell(r.rostered_boarded_right_shift, "rostered_boarded_right_shift", "#2563EB")}
                            {cell(r.rostered_boarded_wrong_shift, "rostered_boarded_wrong_shift", "#9333EA")}
                          </tr>
                        );
                      })}
                      {(!results.rows_by_date_shift_direction || results.rows_by_date_shift_direction.length === 0) && (
                        <tr><td colSpan={11} style={{ textAlign: "center", padding: 32, color: "#64748B" }}>No data for selected filters</td></tr>
                      )}
                    </tbody>
                  </table>
                ) : (
                  <table className="results-table" data-testid="hostel-table">
                    <thead>
                      <tr>
                        <th>Stop</th>
                        <th style={{ textAlign: "right" }}>Rostered</th>
                        <th style={{ textAlign: "right" }}>Boardings</th>
                        <th style={{ textAlign: "right" }}>Right Stop</th>
                        <th style={{ textAlign: "right" }}>Wrong Stop</th>
                        <th style={{ textAlign: "right" }}>Not Boarded</th>
                        <th style={{ textAlign: "right" }}>Not Rostered</th>
                        <th style={{ textAlign: "right" }}>Right Shift</th>
                        <th style={{ textAlign: "right" }}>Wrong Shift</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.rows_by_hostel?.map((r, i) => {
                        const rov = { hostel: r.hostel };
                        const cell = (val, metric, color) => (
                          <td
                            onClick={() => val > 0 && handleDrill(metric, rov)}
                            style={{ textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 500, color, cursor: val > 0 ? "pointer" : "default", textDecoration: val > 0 ? "underline" : "none", textDecorationStyle: "dotted", textUnderlineOffset: 3 }}
                          >{fmt(val)}</td>
                        );
                        return (
                          <tr key={i}>
                            <td style={{ fontWeight: 500 }}>{r.hostel}</td>
                            {cell(r.total_rostered, "total_rostered", "#0F172A")}
                            {cell(r.total_boardings, "total_boardings", "#0F172A")}
                            {cell(r.rostered_boarded_right_stop, "rostered_boarded_right_stop", "#16A34A")}
                            {cell(r.rostered_boarded_wrong_stop, "rostered_boarded_wrong_stop", "#EA580C")}
                            {cell(r.rostered_not_boarded, "rostered_not_boarded", "#DC2626")}
                            {cell(r.not_rostered_but_boarded, "not_rostered_but_boarded", "#EA580C")}
                            {cell(r.rostered_boarded_right_shift, "rostered_boarded_right_shift", "#2563EB")}
                            {cell(r.rostered_boarded_wrong_shift, "rostered_boarded_wrong_shift", "#9333EA")}
                          </tr>
                        );
                      })}
                      {(!results.rows_by_hostel || results.rows_by_hostel.length === 0) && (
                        <tr><td colSpan={9} style={{ textAlign: "center", padding: 32, color: "#64748B" }}>No stop data available</td></tr>
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

          {/* Empty state */}
          {!results && !drillMetric && (
            <div style={{ background: "white", border: "1px solid #E2E8F0", borderRadius: 6, padding: "48px 24px", textAlign: "center" }}>
              <FileJson className="mx-auto mb-3" style={{ width: 40, height: 40, color: "#94A3B8" }} />
              <div style={{ fontSize: 14, fontWeight: 500, color: "#0F172A" }}>Select filters and click "Apply"</div>
              <div style={{ fontSize: 12, color: "#64748B", marginTop: 4 }}>Results will appear here</div>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
